/**
 * ArchivosTab — Archivo cronológico unificado del expediente.
 *
 * 3 sub-tabs:
 *   1) Documentos generados POR el doctor (recetas + órdenes de todo tipo)
 *   2) Resultados APORTADOS por el paciente (PDFs/imágenes subidas en notas de evolución)
 *   3) Adjuntos generales (placeholder para futuro)
 */
import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Chip, Tabs, Tab, CircularProgress, Tooltip, IconButton,
} from '@mui/material';
import {
  Medication as MedicationIcon,
  Science as ScienceIcon,
  InsertDriveFile as FileIcon,
  OpenInNew as OpenIcon,
  Print as PrintIcon,
  Description as DescriptionIcon,
  AttachFile as AttachIcon,
  PictureAsPdf as PdfIcon,
  Image as ImageIcon,
} from '@mui/icons-material';

import { exportConsultaPDF } from './exportConsultaPDF';
import { exportOrdenLaboratorioPDF } from './exportOrdenLaboratorioPDF';
import { exportInterconsultaPDF } from './exportInterconsultaPDF';
import { exportOrdenHospitalizacionPDF } from './exportOrdenHospPDF';
import { exportCertificadoPDF } from './exportCertificadoPDF';
import { exportJustificantePDF } from './exportJustificantePDF';

const API_URL = '/api';

// Metadata por tipo de orden — define color, ícono y exportador PDF
const TIPO_ORDEN = {
  laboratorio:    { label: 'Orden de laboratorio',  color: '#06b6d4', bg: '#ecfeff', emoji: '🧪', exporter: exportOrdenLaboratorioPDF },
  interconsulta:  { label: 'Interconsulta',         color: '#0ea5e9', bg: '#eff6ff', emoji: '🏥', exporter: exportInterconsultaPDF },
  hospitalizacion:{ label: 'Hospitalización',       color: '#ef4444', bg: '#fef2f2', emoji: '🛏', exporter: exportOrdenHospitalizacionPDF },
  certificado:    { label: 'Certificado médico',    color: '#10b981', bg: '#ecfdf5', emoji: '📋', exporter: exportCertificadoPDF },
  justificante:   { label: 'Justificante médico',   color: '#f59e0b', bg: '#fffbeb', emoji: '📅', exporter: exportJustificantePDF },
  libre:          { label: 'Orden libre',           color: '#64748b', bg: '#f8fafc', emoji: '✏', exporter: null },
};

export default function ArchivosTab({ expediente, paciente, userId }) {
  const [tabSub, setTabSub] = useState(0);
  const [recetas, setRecetas] = useState([]);
  const [ordenes, setOrdenes] = useState([]);
  const [archivosAdjuntos, setArchivosAdjuntos] = useState([]);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    if (!expediente?.id) return;
    let cancelado = false;
    (async () => {
      setCargando(true);
      try {
        const headers = { 'X-User-Id': userId };
        const [rCompleto, rOrdenes, rArchivos] = await Promise.all([
          fetch(`${API_URL}/emr/expedientes/${expediente.id}/completo`, { headers }),
          fetch(`${API_URL}/emr/expedientes/${expediente.id}/ordenes-todas`, { headers }),
          fetch(`${API_URL}/emr/expedientes/${expediente.id}/archivos-adjuntos`, { headers }),
        ]);
        if (cancelado) return;
        if (rCompleto.ok) {
          const data = await rCompleto.json();
          setRecetas((data.recetas || []).sort(
            (a, b) => new Date(b.fecha_emision) - new Date(a.fecha_emision)
          ));
        }
        if (rOrdenes.ok) {
          const data = await rOrdenes.json();
          setOrdenes(data.ordenes || []);
        }
        if (rArchivos.ok) {
          const data = await rArchivos.json();
          setArchivosAdjuntos(data.archivos || []);
        }
      } catch (e) {
        console.error('[ARCHIVOS] error:', e);
      } finally {
        if (!cancelado) setCargando(false);
      }
    })();
    return () => { cancelado = true; };
  }, [expediente?.id, userId]);

  if (cargando) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  const totalGenerados = recetas.length + ordenes.length;

  return (
    <Box>
      <Tabs
        value={tabSub} onChange={(_, v) => setTabSub(v)}
        sx={{
          mb: 2, borderBottom: '1px solid #e2e8f0',
          '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 },
        }}
      >
        <Tab
          label={`Documentos generados (${totalGenerados})`}
          icon={<DescriptionIcon />} iconPosition="start"
        />
        <Tab
          label={`Resultados del paciente (${archivosAdjuntos.length})`}
          icon={<AttachIcon />} iconPosition="start"
        />
        <Tab
          label="Adjuntos generales"
          icon={<FileIcon />} iconPosition="start"
        />
      </Tabs>

      {tabSub === 0 && (
        <SeccionDocumentosGenerados
          recetas={recetas}
          ordenes={ordenes}
          paciente={paciente}
        />
      )}
      {tabSub === 1 && <SeccionResultadosPaciente archivos={archivosAdjuntos} />}
      {tabSub === 2 && <SeccionAdjuntosGenerales />}
    </Box>
  );
}

// ============================================================================
// Sub-tab 1: Documentos generados por el doctor
// ============================================================================
function SeccionDocumentosGenerados({ recetas, ordenes, paciente }) {
  // Unificar recetas + órdenes en un único stream cronológico
  const items = [
    ...recetas.map(r => ({
      tipo: 'receta',
      id: `rx-${r.id}`,
      fecha: r.fecha_emision,
      data: r,
    })),
    ...ordenes.map(o => ({
      tipo: 'orden',
      id: `ord-${o.id}`,
      fecha: o.created_at,
      data: o,
    })),
  ].sort((a, b) => new Date(b.fecha) - new Date(a.fecha));

  if (items.length === 0) {
    return (
      <VacioMsg
        icono={<DescriptionIcon />}
        texto="Sin documentos generados"
        subtexto="Las recetas, órdenes de laboratorio, interconsultas, certificados y justificantes que generes aparecerán aquí."
      />
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {items.map(item => (
        item.tipo === 'receta'
          ? <RecetaCard key={item.id} receta={item.data} paciente={paciente} />
          : <OrdenCard key={item.id} orden={item.data} paciente={paciente} />
      ))}
    </Box>
  );
}

function RecetaCard({ receta, paciente }) {
  const meds = Array.isArray(receta.medicamentos) ? receta.medicamentos : [];
  const fecha = receta.fecha_emision
    ? new Date(receta.fecha_emision).toLocaleDateString('es', { day: '2-digit', month: 'short', year: 'numeric' })
    : '—';

  const handleImprimir = () => {
    try {
      exportConsultaPDF({
        paciente,
        consulta: { id: receta.consulta_id, fecha_consulta: receta.fecha_emision },
        diagnosticos: [],
        medicamentos: meds,
      });
    } catch (e) {
      console.error('[RECETA-PDF]', e);
      alert('No se pudo generar el PDF de la receta');
    }
  };

  return (
    <Paper sx={{
      p: 1.2, borderRadius: 2,
      border: '1px solid #e9d5ff',
      borderLeft: '4px solid #8b5cf6',
      background: '#fff',
      transition: 'all 0.15s',
      '&:hover': { boxShadow: '0 2px 12px rgba(139, 92, 246, 0.15)' },
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <MedicationIcon sx={{ fontSize: 20, color: '#8b5cf6', flexShrink: 0 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7, flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: '#581c87' }}>
              Receta médica
            </Typography>
            <Chip
              label={receta.numero_receta} size="small"
              sx={{ fontSize: 10, height: 20, background: '#faf5ff', color: '#6d28d9', fontWeight: 700 }}
            />
            <Chip
              label={`${meds.length} med${meds.length !== 1 ? 's' : ''}`} size="small"
              sx={{ fontSize: 10, height: 20, background: '#ede9fe', color: '#5b21b6' }}
            />
          </Box>
          <Typography sx={{ fontSize: 10.5, color: '#64748b', mt: 0.2 }}>
            {fecha} · {meds.slice(0, 3).map(m => m.nombre).filter(Boolean).join(', ')}
            {meds.length > 3 && ` +${meds.length - 3} más`}
          </Typography>
        </Box>
        <Tooltip title="Imprimir receta">
          <IconButton size="small" onClick={handleImprimir} sx={{ color: '#8b5cf6' }}>
            <PrintIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Paper>
  );
}

function OrdenCard({ orden, paciente }) {
  const meta = TIPO_ORDEN[orden.tipo] || TIPO_ORDEN.libre;
  const fecha = orden.created_at
    ? new Date(orden.created_at).toLocaleDateString('es', { day: '2-digit', month: 'short', year: 'numeric' })
    : '—';

  // Resumen por tipo
  let resumen = '';
  const cont = orden.contenido || {};
  if (orden.tipo === 'laboratorio') {
    const ests = cont.estudios || [];
    resumen = `${ests.length} estudio${ests.length !== 1 ? 's' : ''}`
      + (ests.length > 0 ? `: ${ests.slice(0, 2).map(e => e.nombre).join(', ')}${ests.length > 2 ? '…' : ''}` : '');
  } else if (orden.tipo === 'interconsulta') {
    resumen = cont.especialidad ? `${cont.especialidad}` : 'Especialidad no indicada';
  } else if (orden.tipo === 'hospitalizacion') {
    resumen = (cont.diagnostico || cont.indicaciones_hospitalarias || '').toString().substring(0, 80);
  } else if (orden.tipo === 'certificado' || orden.tipo === 'justificante') {
    resumen = (cont.diagnostico || cont.texto || '').toString().substring(0, 80);
  } else {
    resumen = (cont.texto || '').toString().substring(0, 80);
  }

  const handleImprimir = async () => {
    if (!meta.exporter) {
      alert('Esta orden no tiene exportador PDF disponible');
      return;
    }
    try {
      await meta.exporter({
        paciente,
        orden,
        imprimir: true,
      });
    } catch (e) {
      console.error('[ORDEN-PDF]', e);
      alert(`No se pudo generar el PDF: ${e.message || 'error'}`);
    }
  };

  return (
    <Paper sx={{
      p: 1.2, borderRadius: 2,
      border: `1px solid ${meta.color}33`,
      borderLeft: `4px solid ${meta.color}`,
      background: '#fff',
      transition: 'all 0.15s',
      '&:hover': { boxShadow: `0 2px 12px ${meta.color}25` },
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Box sx={{ fontSize: 18, lineHeight: 1, flexShrink: 0 }}>{meta.emoji}</Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7, flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: meta.color }}>
              {meta.label}
            </Typography>
            {orden.consulta_fecha && (
              <Chip
                label={`Consulta ${new Date(orden.consulta_fecha).toLocaleDateString('es', { day: '2-digit', month: 'short' })}`}
                size="small"
                sx={{ fontSize: 10, height: 20, background: meta.bg, color: meta.color, fontWeight: 700 }}
              />
            )}
          </Box>
          <Typography sx={{ fontSize: 10.5, color: '#64748b', mt: 0.2 }}>
            {fecha}{resumen && ` · ${resumen}`}
          </Typography>
          {orden.indicaciones && (
            <Typography sx={{ fontSize: 10, color: '#94a3b8', mt: 0.2, fontStyle: 'italic' }}>
              {orden.indicaciones.substring(0, 100)}{orden.indicaciones.length > 100 ? '…' : ''}
            </Typography>
          )}
        </Box>
        {meta.exporter && (
          <Tooltip title="Imprimir / Generar PDF">
            <IconButton size="small" onClick={handleImprimir} sx={{ color: meta.color }}>
              <PrintIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Paper>
  );
}

// ============================================================================
// Sub-tab 2: Resultados aportados por el paciente
// ============================================================================
function SeccionResultadosPaciente({ archivos }) {
  if (archivos.length === 0) {
    return (
      <VacioMsg
        icono={<AttachIcon />}
        texto="Sin resultados del paciente"
        subtexto="Cuando el paciente traiga PDFs o imágenes con resultados de laboratorio (en notas de evolución), aparecerán acá para consulta posterior."
      />
    );
  }

  // Ordenar por fecha de consulta descendente
  const ordenados = [...archivos].sort((a, b) =>
    new Date(b.consulta_fecha) - new Date(a.consulta_fecha)
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {ordenados.map((a, i) => {
        const esPdf = a.archivo_tipo === 'application/pdf';
        const fecha = a.consulta_fecha
          ? new Date(a.consulta_fecha).toLocaleDateString('es', { day: '2-digit', month: 'short', year: 'numeric' })
          : '—';
        const totalAnormales = (a.estudios_extraidos || []).reduce(
          (acc, est) => acc + (est.anormales || 0), 0
        );

        return (
          <Paper
            key={i}
            component="a"
            href={`${API_URL}${a.archivo_url}`}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              p: 1.2, borderRadius: 2,
              borderLeft: `4px solid ${esPdf ? '#dc2626' : '#0891b2'}`,
              border: `1px solid ${esPdf ? '#fecaca' : '#a5f3fc'}`,
              background: esPdf
                ? 'linear-gradient(135deg, #fef2f2 0%, #fff7ed 100%)'
                : 'linear-gradient(135deg, #ecfeff 0%, #eff6ff 100%)',
              cursor: 'pointer', textDecoration: 'none',
              transition: 'all 0.15s',
              display: 'block',
              '&:hover': {
                filter: 'brightness(0.97)',
                boxShadow: `0 4px 14px ${esPdf ? 'rgba(239, 68, 68, 0.18)' : 'rgba(8, 145, 178, 0.18)'}`,
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              {esPdf
                ? <PdfIcon sx={{ fontSize: 22, color: '#dc2626', flexShrink: 0 }} />
                : <ImageIcon sx={{ fontSize: 22, color: '#0891b2', flexShrink: 0 }} />}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7, flexWrap: 'wrap' }}>
                  <Typography sx={{
                    fontSize: 12.5, fontWeight: 800,
                    color: esPdf ? '#991b1b' : '#0c4a6e',
                    textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap',
                  }}>
                    {a.archivo_nombre || 'Archivo'}
                  </Typography>
                  <Chip
                    label={`${(a.estudios_extraidos || []).length} estudio${(a.estudios_extraidos || []).length !== 1 ? 's' : ''}`}
                    size="small"
                    sx={{
                      fontSize: 10, height: 20, fontWeight: 700,
                      background: '#fff',
                      color: esPdf ? '#991b1b' : '#0c4a6e',
                      border: `1px solid ${esPdf ? '#fecaca' : '#a5f3fc'}`,
                    }}
                  />
                  {totalAnormales > 0 && (
                    <Chip
                      label={`${totalAnormales} fuera de rango`} size="small"
                      sx={{ fontSize: 10, height: 20, background: '#fef2f2', color: '#b91c1c', fontWeight: 700 }}
                    />
                  )}
                </Box>
                <Typography sx={{ fontSize: 10.5, color: '#64748b', mt: 0.2 }}>
                  Consulta del {fecha}
                  {a.consulta_motivo && ` · ${a.consulta_motivo.substring(0, 60)}${a.consulta_motivo.length > 60 ? '…' : ''}`}
                </Typography>
                {(a.estudios_extraidos || []).length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.3, mt: 0.4 }}>
                    {a.estudios_extraidos.slice(0, 4).map((est, j) => (
                      <Chip
                        key={j}
                        label={est.nombre || 'Estudio'}
                        size="small"
                        sx={{
                          fontSize: 9.5, height: 18,
                          background: '#fff',
                          color: '#475569',
                          border: '1px solid #e2e8f0',
                        }}
                      />
                    ))}
                    {a.estudios_extraidos.length > 4 && (
                      <Chip
                        label={`+${a.estudios_extraidos.length - 4} más`} size="small"
                        sx={{ fontSize: 9.5, height: 18, background: '#f1f5f9', color: '#64748b' }}
                      />
                    )}
                  </Box>
                )}
              </Box>
              <Box sx={{
                fontSize: 10, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase',
                color: esPdf ? '#dc2626' : '#0891b2',
                flexShrink: 0,
              }}>
                Abrir →
              </Box>
            </Box>
          </Paper>
        );
      })}
    </Box>
  );
}

// ============================================================================
// Sub-tab 3: Adjuntos generales (placeholder futuro)
// ============================================================================
function SeccionAdjuntosGenerales() {
  return (
    <VacioMsg
      icono={<FileIcon />}
      texto="Adjuntos generales"
      subtexto="Próximamente: identificaciones, autorizaciones de aseguradora, fotos clínicas y otros documentos del paciente que no son resultados de laboratorio."
    />
  );
}

function VacioMsg({ icono, texto, subtexto }) {
  return (
    <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3, background: '#f8fafc', border: '1px dashed #cbd5e1' }}>
      {React.cloneElement(icono, { sx: { fontSize: 40, color: '#cbd5e1', mb: 1 } })}
      <Typography sx={{ color: '#475569', fontSize: 14, fontWeight: 700 }}>{texto}</Typography>
      {subtexto && <Typography sx={{ color: '#94a3b8', fontSize: 12, mt: 0.5, maxWidth: 480, mx: 'auto' }}>{subtexto}</Typography>}
    </Paper>
  );
}
